import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "./client";
import type { Run, RunCreateRequest, ScenarioAlertPreview, VerifyQuery } from "./types";

const invalidateRuns = (qc: ReturnType<typeof useQueryClient>) =>
  qc.invalidateQueries({ queryKey: ["runs"] });

export function useRuns() {
  return useQuery({
    queryKey: ["runs"],
    queryFn: async () => {
      const body = await apiFetch<{ runs: Run[] }>("/api/v1/runs");
      return body.runs;
    },
    refetchInterval: 5000,
  });
}

export function useRun(runId: string | undefined) {
  return useQuery({
    queryKey: ["run", runId],
    queryFn: () => apiFetch<Run>(`/api/v1/runs/${runId}`),
    enabled: !!runId,
  });
}

export function useStartRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RunCreateRequest) =>
      apiFetch<Run>("/api/v1/runs/start", { method: "POST", body }),
    onSuccess: () => invalidateRuns(qc),
  });
}

export function useStopRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (runId: string) =>
      apiFetch<Run>(`/api/v1/runs/${runId}/stop`, { method: "POST" }),
    onSuccess: (run) => {
      invalidateRuns(qc);
      qc.setQueryData(["run", run.id], run);
    },
  });
}

export function useRunAlerts(runId: string | undefined) {
  return useQuery({
    queryKey: ["run-alerts", runId],
    queryFn: async () => {
      const body = await apiFetch<{ alerts: ScenarioAlertPreview[] }>(
        `/api/v1/runs/${runId}/alerts`,
      );
      return body.alerts;
    },
    enabled: !!runId,
  });
}

export function useVerifyQueries(runId: string | undefined) {
  return useQuery({
    queryKey: ["run-verify-queries", runId],
    queryFn: async () => {
      // Multi-backend runs return queries keyed by backend_id
      // ({queries: {backend_id: [...]}}). Legacy single-backend runs
      // may still return a flat list ({queries: [...]}). Handle both
      // by flattening into a single VerifyQuery[] — the panel renders
      // them as one list. A per-backend UX is a follow-up.
      const body = await apiFetch<{
        queries: VerifyQuery[] | Record<string, VerifyQuery[]>;
      }>(`/api/v1/runs/${runId}/verify_queries`);
      if (Array.isArray(body.queries)) {
        return body.queries;
      }
      return Object.values(body.queries).flat();
    },
    enabled: !!runId,
  });
}

export function useRebaseRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (runId: string) =>
      apiFetch<Run>(`/api/v1/runs/${runId}/rebase`, { method: "POST" }),
    onSuccess: (run) => {
      invalidateRuns(qc);
      qc.setQueryData(["run", run.id], run);
    },
  });
}

export function useDeleteRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (runId: string) =>
      apiFetch<void>(`/api/v1/runs/${runId}`, { method: "DELETE" }),
    onSuccess: (_, runId) => {
      invalidateRuns(qc);
      qc.removeQueries({ queryKey: ["run", runId] });
    },
  });
}
